import {
  MAX_QUOTE_LINES,
  MAX_QUOTE_QUANTITY,
  saveOrderQuoteRequestSchema,
  type OrderQuote,
  type ProductSummary,
  type SaveOrderQuoteRequest,
} from '@avrahami/contracts';
import { he } from '../i18n/he';
import { parseWith, textOrNull, type FieldErrors, type Parsed } from './forms';
import { minorToShekels, shekelsToMinor } from './money';

/**
 * The store's quote for a request as it is edited in the tenant admin (docs/TENANT_ADMIN.md
 * "The quote"): the texts people type, the totals they see while typing, and the request the
 * API saves. The API works the amounts out again; these are for the screen.
 */

export interface QuoteLineDraft {
  /** Tells the rows apart while lines are added and removed. */
  key: string;
  /** Null for a line the store writes itself (delivery, installation). */
  productId: string | null;
  title: string;
  sku: string;
  quantity: string;
  price: string;
}

export interface QuoteDraft {
  lines: QuoteLineDraft[];
  validUntil: string;
  paymentTerms: string;
  notes: string;
}

let nextKey = 0;
function lineKey(): string {
  nextKey += 1;
  return `line-${String(nextKey)}`;
}

export function quoteDraft(quote: OrderQuote): QuoteDraft {
  return {
    lines: quote.lines.map((line) => ({
      key: lineKey(),
      productId: line.productId,
      title: line.title,
      sku: line.sku ?? '',
      quantity: String(line.quantity),
      price: minorToShekels(line.unitPriceMinor),
    })),
    validUntil: quote.validUntil ?? '',
    paymentTerms: quote.paymentTerms ?? '',
    notes: quote.notes ?? '',
  };
}

/** A product of the store as a new line: one unit at its sale price (empty while it has none). */
export function productLine(product: ProductSummary): QuoteLineDraft {
  return {
    key: lineKey(),
    productId: product.id,
    title: product.title ?? product.name,
    sku: product.sku,
    quantity: '1',
    price: minorToShekels(product.amountMinor),
  };
}

/** An empty line of the store's own. */
export function customLine(): QuoteLineDraft {
  return { key: lineKey(), productId: null, title: '', sku: '', quantity: '1', price: '' };
}

/** A quantity as typed: a whole number of units, NaN when it is not one. */
function quantityOf(text: string): number {
  const value = text.trim();
  return /^\d{1,6}$/.test(value) ? Number(value) : Number.NaN;
}

/** A line's amount while it is typed; null while its quantity or price is not a number yet. */
export function lineTotal(line: QuoteLineDraft): number | null {
  const quantity = quantityOf(line.quantity);
  const price = shekelsToMinor(line.price);
  if (price === null || !Number.isFinite(price) || !Number.isFinite(quantity)) return null;
  return price * quantity;
}

/**
 * The quote's totals as typed: the lines with an amount, VAT included, and the VAT in it at the
 * store's rate (in percent), as the API rounds it.
 */
export function quoteTotals(
  draft: QuoteDraft,
  vatRatePercent: number,
): { totalMinor: number; vatMinor: number; beforeVatMinor: number } {
  const totalMinor = draft.lines.reduce((sum, line) => sum + (lineTotal(line) ?? 0), 0);
  const vatRateBp = Math.round(vatRatePercent * 100);
  const vatMinor = Math.round((totalMinor * vatRateBp) / (10_000 + vatRateBp));
  return { totalMinor, vatMinor, beforeVatMinor: totalMinor - vatMinor };
}

/**
 * The save request, or the mistakes per field (`lines.<index>.<field>`, `validUntil`, ...). The
 * lines are checked here first, so each mistake shows on its own field.
 */
export function quoteRequest(draft: QuoteDraft, version: number): Parsed<SaveOrderQuoteRequest> {
  const errors: Record<string, string> = {};
  if (draft.lines.length === 0) errors['lines'] = he.quote.noLines;
  if (draft.lines.length > MAX_QUOTE_LINES) errors['lines'] = he.quote.tooManyLines;
  draft.lines.forEach((line, index) => {
    const at = `lines.${String(index)}`;
    const quantity = quantityOf(line.quantity);
    if (!Number.isFinite(quantity) || quantity < 1 || quantity > MAX_QUOTE_QUANTITY) {
      errors[`${at}.quantity`] = he.quote.badQuantity;
    }
    const price = shekelsToMinor(line.price);
    if (price === null || !Number.isFinite(price)) errors[`${at}.price`] = he.quote.badPrice;
    if (line.productId === null && line.title.trim() === '') {
      errors[`${at}.title`] = he.quote.needsTitle;
    }
  });
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return parseWith(saveOrderQuoteRequestSchema, {
    version,
    lines: draft.lines.map((line) => {
      const fields = {
        quantity: quantityOf(line.quantity),
        unitPriceMinor: shekelsToMinor(line.price),
      };
      return line.productId === null
        ? { productId: null, title: line.title, sku: textOrNull(line.sku), ...fields }
        : { productId: line.productId, ...fields };
    }),
    validUntil: textOrNull(draft.validUntil),
    paymentTerms: draft.paymentTerms,
    notes: draft.notes,
  });
}

/** The mistakes of one line, by field. */
export function lineErrors(errors: FieldErrors, index: number) {
  const at = `lines.${String(index)}`;
  return {
    title: errors[`${at}.title`],
    sku: errors[`${at}.sku`],
    quantity: errors[`${at}.quantity`],
    price: errors[`${at}.price`] ?? errors[`${at}.unitPriceMinor`],
    product: errors[`${at}.productId`] ?? errors[at],
  };
}
